import React, {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import Snackbar from '~/components/Snackbar';
import { AuthUser } from '~/data/models/user';
import { t } from '~/utils/i18n';
import {
  identityFromAuthUser,
  loaderSrcFromEnv,
  mintTicket,
  openChat,
  syncIdentity,
} from '~/utils/respondWidget';
import { trackError } from '~/utils/sentry';

// NOTE: this file uses respond.io. The hook and context names retain "Intercom"
// for compatibility with the 18 consumer screens and src/context/auth.tsx;
// renaming them is a separate piece of work. The native half of this context
// (src/context/intercom.tsx) is still Intercom and is untouched.
//
// Intercom is deprecated: nothing of it may feed this path. In particular
// authUser.externalTokens.intercom is Intercom's identity-verification JWT and
// must never be passed to respond.io, which verifies nothing it is given.

export const INITIAL_STATE: IntercomContextAPI = {
  unReadMessageCount: 0,
  onLogin: async () => {},
  onHelpCenter: async () => {},
  onMessenger: async () => {},
  onLogout: async () => {},
  onLoginAnon: async () => {},
};

const IntercomContext = createContext<IntercomContextAPI>(INITIAL_STATE);

export const IntercomProvider: FC<PropsWithChildren> = ({ children }) => {
  // respond.io's widget exposes no unread-count event, so the badge reads 0.
  const unReadMessageCount = 0;
  const loaderSrc = loaderSrcFromEnv();

  // Chat is never allowed to break the auth flow. auth.tsx awaits these inside
  // its own login/logout, so a rejection here would skip clearing storage and
  // navigating, leaving the user on a blank page.
  const safely = useCallback(async (run: () => Promise<void>) => {
    try {
      await run();
    } catch (e) {
      trackError(e);
    }
  }, []);

  const onLoginAnon = useCallback(async () => {
    await safely(() => syncIdentity({}, loaderSrc, mintTicket));
  }, [loaderSrc, safely]);

  const onLogin = useCallback(
    async (authUser: AuthUser) => {
      if (!authUser.email) {
        // No email yet (an incomplete profile): stay unidentified rather than
        // booting the widget without the identifier it matches contacts on.
        return;
      }

      await safely(() =>
        syncIdentity(identityFromAuthUser(authUser), loaderSrc, mintTicket),
      );
    },
    [loaderSrc, safely],
  );

  const onLogout = useCallback(async () => {
    // Re-boot anonymously. The backend-served shell clears respond.io's storage
    // for an anonymous boot, so the previous user's transcript does not remain
    // visible to the next person on this browser.
    await safely(() => syncIdentity({}, loaderSrc, mintTicket));
  }, [loaderSrc, safely]);

  const open = useCallback(async () => {
    if (!openChat()) {
      Snackbar.show(t('intercom.helpCenterError'));
    }
  }, []);

  const value = useMemo(
    () => ({
      unReadMessageCount,
      onLogin,
      onLogout,
      onLoginAnon,
      onHelpCenter: open,
      onMessenger: open,
    }),
    [onLogin, onLogout, onLoginAnon, open],
  );

  return (
    <IntercomContext.Provider value={value}>
      {children}
    </IntercomContext.Provider>
  );
};

export function useIntercom(): IntercomContextAPI {
  return useContext(IntercomContext);
}
